import type { PostHog } from 'posthog-js/dist/module.no-external';
import { analyticsAllowed, analyticsOperation, analyticsPage, sanitizeAnalyticsEvent, sanitizeExceptionList, type AnalyticsSurface } from './analytics-privacy';

export interface AnalyticsConfig { token: string | null; environment: 'production' | 'development' | 'test' }
export type AnalyticsConsent = 'allowed' | 'declined' | null;
const consentKey='obelisk.analytics-consent.v1';
let config:AnalyticsConfig={token:null,environment:'development'};
let surface:AnalyticsSurface='workspace';
let blocked=true;
let client:PostHog|undefined;
let pending:Promise<void>|undefined;
let lastPage:string|null=null;
let monitoringInstalled=false;
let eventsThisPage=0;

export function analyticsConsent():AnalyticsConsent {
  try { const value=localStorage.getItem(consentKey); return value==='allowed'||value==='declined' ? value : null; } catch { return null; }
}
export function browserRejectsAnalytics() { return navigator.doNotTrack==='1' || (navigator as Navigator & {globalPrivacyControl?:boolean}).globalPrivacyControl===true; }
function permitted() {
  let support=false; try { support=localStorage.getItem('obelisk.support-session')==='active'; } catch { /* storage is optional */ }
  return analyticsAllowed({configured:Boolean(config.token),consent:analyticsConsent(),blocked,support,privacySignal:browserRejectsAnalytics(),page:analyticsPage(window.location.pathname+window.location.search,surface)});
}
export function setAnalyticsConsent(value:Exclude<AnalyticsConsent,null>) {
  try { localStorage.setItem(consentKey,value); } catch { return; }
  if(value==='declined') { client?.opt_out_capturing(); client?.reset(); lastPage=null; }
  window.dispatchEvent(new Event('obelisk:analytics-consent'));
}
export function configureAnalytics(next:AnalyticsConfig,appSurface:AnalyticsSurface,isBlocked:boolean) {
  config=next; surface=appSurface; blocked=isBlocked;
  if(!permitted()) { lastPage=null; return Promise.resolve(); }
  if(client) { client.opt_in_capturing({captureEventName:false}); return Promise.resolve(); }
  if(pending) return pending;
  pending=import('posthog-js/dist/module.no-external').then(({default:posthog})=>{
    if(!permitted()) return;
    posthog.init(config.token!,{
      api_host:'https://us.i.posthog.com',ui_host:'https://us.posthog.com',
      autocapture:false,capture_pageview:false,capture_pageleave:false,capture_dead_clicks:false,
      capture_exceptions:false,capture_heatmaps:false,capture_performance:false,rageclick:false,
      disable_session_recording:true,disable_surveys:true,disable_external_dependency_loading:true,
      disable_conversations:true,disable_product_tours:true,disableDeviceModel:true,
      advanced_disable_flags:true,person_profiles:'never',persistence:'memory',disable_persistence:true,
      save_referrer:false,save_campaign_params:false,cross_subdomain_cookie:false,respect_dnt:true,
      opt_out_useragent_filter:config.environment==='test',
      request_batching:false,mask_all_text:true,mask_all_element_attributes:true,
      before_send:event=>permitted() && event ? sanitizeAnalyticsEvent(event) : null,
    });
    client=posthog;
    installMonitoring();
  }).catch(error=>{ if(config.environment!=='production') console.warn('Optional analytics could not initialize.',error); }).finally(()=>{pending=undefined;});
  return pending;
}

function capture(event:string,properties:Record<string,unknown>={}) {
  if(!client || !permitted() || eventsThisPage>=200) return;
  eventsThisPage++;
  client.capture(event,{app:'obelisk',surface,environment:config.environment,page:analyticsPage(window.location.pathname+window.location.search,surface),...properties});
}
export function trackAnalyticsPage() {
  if(!client || !permitted()) { lastPage=null; return; }
  const page=analyticsPage(window.location.pathname+window.location.search,surface);
  // StrictMode and query edits must not double-count a page view.
  if(page===lastPage) return;
  lastPage=page; capture('obelisk_page_viewed');
}
export function measureRequest(method:string,url:string) {
  const started=performance.now(); const operation=analyticsOperation(method,url);
  const page=analyticsPage(window.location.pathname+window.location.search,surface);
  const optedIn=permitted();
  return (status:number)=>{
    if(!optedIn || !operation || !page) return;
    capture('obelisk_request_finished',{page,operation,status,duration_ms:Math.min(600_000,Math.round(performance.now()-started)),outcome:status===0?'network_error':status>=500?'error':status>=400?'rejected':'success'});
  };
}

/**
 * Send one crash to error monitoring, sanitised. The window listeners use it
 * for uncaught errors, and the error boundaries for the render crashes they
 * catch, which never reach `window` in a production build (FC-5).
 */
export function reportException(error:unknown) {
  if(!permitted()) return;
  // Never forward the message, cause, custom properties or user-authored strings.
  const frames=error instanceof Error ? (error.stack??'').split('\n').flatMap(line=>{
    const match=line.match(/(https?:\/\/[^\s)]+\/assets\/[a-zA-Z0-9_-]+\.js):(\d+):(\d+)/);
    return match ? [{filename:match[1],lineno:Number(match[2]),colno:Number(match[3])}] : [];
  }) : [];
  if(!client || eventsThisPage>=200) return;
  eventsThisPage++;
  const type=sanitizeExceptionList([{type:error instanceof Error ? error.name : 'Error'}])[0]?.type as string;
  const safe=new Error(`Unhandled ${type}`); safe.name=type;
  safe.stack=`${type}: Unhandled ${type}\n${frames.map(frame=>`    at ${frame.filename}:${frame.lineno}:${frame.colno}`).join('\n')}`;
  client.captureException(safe,{app:'obelisk',surface,environment:config.environment,page:analyticsPage(window.location.pathname+window.location.search,surface)});
}

function installMonitoring() {
  if(monitoringInstalled) return; monitoringInstalled=true;
  window.addEventListener('error',event=>reportException(event.error));
  window.addEventListener('unhandledrejection',event=>reportException(event.reason));
  // Only numbers and fixed metric names are sent; no DOM elements or resource URLs.
  const page=analyticsPage(window.location.pathname+window.location.search,surface);
  void import('web-vitals').then(({onLCP,onINP,onCLS})=>{
    const reportVital=({name,value,rating}:{name:string;value:number;rating:string})=>capture('obelisk_web_vital',{page,metric:name,value,rating});
    onLCP(reportVital); onINP(reportVital); onCLS(reportVital);
  }).catch(()=>{});
}

// Apply revocation from another tab immediately, before any following capture.
window.addEventListener('storage',event=>{
  if(event.key===consentKey) { if(analyticsConsent()!=='allowed') {client?.opt_out_capturing();client?.reset();lastPage=null;} window.dispatchEvent(new Event('obelisk:analytics-consent')); }
});
